import { API_BASE_URL } from '../config/constants';

export interface GitLabFile {
  id: string;
  name: string;
  path: string;
  type: 'tree' | 'blob';
  mode: string;
  rawUrl: string;
}

export interface GitLabUploadResult {
  success: boolean;
  file_path: string;
  branch: string;
  url: string;
  updated?: boolean;
}

export interface GitLabFolderResult {
  success: boolean;
  error?: string;
}

export interface GitLabFileContent {
  success: boolean;
  content?: string;
  filePath?: string;
  contentType?: string;
  error?: string;
}

export interface GitLabValidation {
  valid: boolean;
  error?: string;
}

class GitLabService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('authToken');
    console.log('🔍 GitLab Service - Retrieved token from localStorage:', token ? `${token.substring(0, 10)}...` : 'null');
    
    if (!token || token === 'null' || token === 'undefined') {
      throw new Error('No authentication token found. Please log in again.');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async checkToken(): Promise<{ hasToken: boolean }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gitlab/external-token`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to check GitLab token');
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking GitLab token:', error);
      return { hasToken: false };
    }
  }

  async setToken(token: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gitlab/external-token`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save GitLab token');
      }

      return await response.json();
    } catch (error) {
      console.error('Error setting GitLab token:', error);
      throw error;
    }
  }

  async getDefaultRepository(): Promise<{ url: string | null }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/default_gitlab_url`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { url: null };
        }
        throw new Error('Failed to get default GitLab repository');
      }

      const data = await response.json();
      return { url: data.value || null };
    } catch (error) {
      console.error('Error getting default GitLab repository:', error);
      return { url: null };
    }
  }

  async setDefaultRepository(url: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/default_gitlab_url`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ value: url })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save default GitLab repository');
      }

      return { success: true, message: 'Default repository saved successfully' };
    } catch (error) {
      console.error('Error setting default GitLab repository:', error);
      throw error;
    }
  }

  async resolveGitLabUrl(configUrl?: string): Promise<string | null> {
    // If config has a URL, use it
    if (configUrl && configUrl.trim()) {
      return configUrl.trim();
    }

    // Otherwise, try to get the default repository
    try {
      const defaultRepo = await this.getDefaultRepository();
      return defaultRepo.url;
    } catch (error) {
      console.warn('Failed to get default repository:', error);
      return null;
    }
  }

  async getUserRepositories(): Promise<{success: boolean, repositories?: Array<{id: number, name: string, url: string, description?: string}>, error?: string}> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gitlab/user-repositories`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch user repositories');
      }

      const data = await response.json();
      return {
        success: true,
        repositories: data.repositories || []
      };
    } catch (error) {
      console.error('Error fetching user repositories:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async validateUrl(gitlabUrl: string): Promise<GitLabValidation> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gitlab/validate-url`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ gitlabUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to validate GitLab URL');
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating GitLab URL:', error);
      return { valid: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async getFiles(gitlabUrl: string, path: string = '', search: string = ''): Promise<GitLabFile[]> {
    try {
      const params = new URLSearchParams({
        gitlabUrl,
        ...(path && { path }),
        ...(search && { search })
      });

      const response = await fetch(`${API_BASE_URL}/api/gitlab/files?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch GitLab files');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching GitLab files:', error);
      throw error instanceof Error ? error : new Error('Unknown error occurred');
    }
  }

  async getFileContent(gitlabUrl: string, filePath: string): Promise<GitLabFileContent> {
    try {
      const params = new URLSearchParams({
        gitlabUrl,
        filePath
      });

      const response = await fetch(`${API_BASE_URL}/api/gitlab/file-content?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch file content from GitLab');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching file content from GitLab:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async uploadFile(
    gitlabUrl: string, 
    fileName: string, 
    content: string, 
    commitMessage: string = 'Upload document via AI Assistant',
    documentTitle?: string
  ): Promise<GitLabUploadResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gitlab/upload`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          gitlabUrl,
          fileName,
          content,
          commitMessage,
          documentTitle
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file to GitLab');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading file to GitLab:', error);
      throw error;
    }
  }

  async ensureAIDraftsFolder(gitlabUrl: string, documentTitle?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gitlab/ensure-ai-drafts-folder`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ gitlabUrl, documentTitle })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to ensure AI-Drafts folder');
      }

      return await response.json();
    } catch (error) {
      console.error('Error ensuring AI-Drafts folder:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}

export default new GitLabService();